import type { DataGridFilterState, DataGridSortingState, TableFilterState, TableSortingState } from '@ibsys/kit';
import { useCallback, useMemo } from 'react';
import type { ConversationListItem } from '@/entities/conversations';
import type { ExpertReportSettings } from '@/entities/settings';
import { useLazyGridPages } from '@/shared/hooks';
import type { DialogFetchData, DialogMainColumn } from './types';

/** Части ключа кеша запроса списка: их смена перезапрашивает список */
type CacheKey = {
  /** Сортировка грида */
  gridSorting: DataGridSortingState;
  /** Фильтры грида */
  gridFilters: DataGridFilterState;
  /** Отбор раздела поверх пользовательского (выборка, которой ограничен список) */
  sectionFilters: TableFilterState;
  /** Видимые основные колонки */
  visibleMainColumns: DialogMainColumn[];
  /** Пути видимых атрибутов */
  visibleAttributePaths: string[];
  /** Параметры видимых экспертных заключений */
  expertReportSearchParams: ExpertReportSettings[];
  /** Активный режим поиска по содержимому */
  activeContentMode: string | null;
  /** Зафиксированное значение поиска по содержимому */
  committedContentValue: string;
  /** Ручной триггер перезагрузки */
  reloadKey: number;
};

/** Параметры ленивой постраничной загрузки списка диалогов */
type Params = {
  fetchData: DialogFetchData;
  /** Действующие фильтры выборки (с наложенным слоем перехода) */
  filters: TableFilterState;
  /** Действующая сортировка выборки */
  sorting: TableSortingState;
  /** Части ключа кеша запроса */
  cacheKey: CacheKey;
  /** Колбэк первой непустой загрузки — для инициализации зависящего от данных UI */
  onLoaded?: (data: ConversationListItem[]) => void;
};

/** Результат ленивой загрузки списка */
type Result = {
  /** Общее число строк выборки (до постраничности) */
  totalCount: number;
  /** Версия кеша запроса — передаётся гриду для перезапроса показанного диапазона */
  cacheVersion: string;
  /** Ключ пересоздания грида: меняется только когда список пуст */
  gridKey: string;
  /** Загрузка одной страницы для DataGrid lazy */
  fetchPage: (offset: number, limit: number, signal: AbortSignal) => Promise<ConversationListItem[]>;
};

/**
 * Ленивая постраничная загрузка списка диалогов для DataGrid: собирает сигнатуру запроса из
 * условий выборки списка и отдаёт окна выдачи общему механизму подгрузки по прокрутке
 * ({@link useLazyGridPages}).
 */
export const useLazyPageFetch = ({ fetchData, filters, sorting, cacheKey, onLoaded }: Params): Result => {
  const {
    gridSorting,
    gridFilters,
    sectionFilters,
    visibleMainColumns,
    visibleAttributePaths,
    expertReportSearchParams,
    activeContentMode,
    committedContentValue,
    reloadKey,
  } = cacheKey;

  const cacheVersion = useMemo(
    () =>
      `${JSON.stringify(gridSorting)}|${JSON.stringify(gridFilters)}|${JSON.stringify(sectionFilters)}|${JSON.stringify(visibleMainColumns)}|${JSON.stringify(visibleAttributePaths)}|${JSON.stringify(expertReportSearchParams)}|${activeContentMode ?? ''}|${committedContentValue}|${reloadKey}`,
    [
      gridSorting,
      gridFilters,
      sectionFilters,
      visibleMainColumns,
      visibleAttributePaths,
      expertReportSearchParams,
      activeContentMode,
      committedContentValue,
      reloadKey,
    ],
  );

  const loadPage = useCallback(
    (offset: number, limit: number) => fetchData({ offset, limit, filters, sorting }),
    [fetchData, filters, sorting],
  );

  const { totalCount, gridKey, fetchPage } = useLazyGridPages<ConversationListItem>({
    cacheVersion,
    fetchPage: loadPage,
    onLoaded,
  });

  return { totalCount, cacheVersion, gridKey, fetchPage };
};
